import {Alert, Separator} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {
  VehicleForm as VFKit,
  VehicleSubSections as VFSubSection,
  VehicleFormDefaultLayout,
  isTrailerOrSemitrailer,
  useIntegrationsEnabled,
  useIsCardExpandable,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {settingsRoutes, vehiclesRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {composePath, openInNewTab, useNavigate} from 'shared/web';

import {AdditionalInfoWidget} from '../components/AdditionalInfoWidget';

interface ServiceBasicInformationProps {
  disableVinDecoder?: boolean;
  vehicleId: string | undefined;
  shouldShowVehicleClass?: boolean;
  isEmbedded?: boolean;
  infoAlert?: {
    title: string;
    message: string;
  };
}

export function ServiceBasicInformation({
  disableVinDecoder,
  vehicleId,
  shouldShowVehicleClass = true,
  isEmbedded,
  infoAlert,
}: ServiceBasicInformationProps) {
  const navigate = useNavigate();
  const isExpandable = useIsCardExpandable();
  const {isCebiaAutotracerAndReportEnabled, isCebiaFeatureDecoderEnabled} =
    useIntegrationsEnabled();

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  return (
    <VehicleFormDefaultLayout>
      <VehicleFormDefaultLayout.Left>
        <VStack spacing={4}>
          <VFKit.Section
            data-testid="basic-information"
            isExpandable={isExpandable}
            title={isExpandable ? i18n.t('general.labels.basicInformation') : undefined}
          >
            <VFKit.SubSection data-testid="vehicle-identification">
              <Show when={infoAlert}>
                <Alert variant="info" title={infoAlert?.title} message={infoAlert?.message} />
              </Show>
              <VFKit.VinDecoderField
                variant="service"
                // Embedded, the caller already offered these matches - and picking one here
                // navigates to /vehicles, discarding the host page.
                shouldRecommendVehicles={!disableVinDecoder && !isEmbedded}
                isDisabled={disableVinDecoder}
                onSelect={(vehicle, selectionType) => {
                  if (selectionType === 'createServiceProcess') {
                    navigate(
                      composePath(vehiclesRoutes.createServiceFromSaleVehicleV2, {
                        params: {
                          id: vehicle.id,
                        },
                        isSearchParamsPreserved: false,
                      })
                    );
                  }
                }}
              />
              <VFKit.BodyTypeChips />
            </VFKit.SubSection>
            <VFSubSection.VehicleBasics shouldShowVehicleClass={shouldShowVehicleClass} />
            <VFSubSection.VehicleRegistration />
            <Separator spacing={1} />
            <VFSubSection.EngineBasics />
            <VFSubSection.VehicleConfiguration />
            <VFSubSection.VehicleStyling />
            <VFKit.HandDriveChips />
          </VFKit.Section>

          <VFKit.Section
            data-testid="additional-information"
            isExpandable={isExpandable}
            title={i18n.t('general.labels.additionalInformation')}
          >
            <Separator spacing={1} />
            <VFSubSection.EngineDetails />
            <VFKit.ShowWhen
              name="type"
              condition={(type) =>
                !isTrailerOrSemitrailer(type?.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
              }
            >
              <Separator spacing={1} />
            </VFKit.ShowWhen>
            <VFSubSection.ConsumptionEmissions />
            <VFKit.ShowWhen
              name="type"
              condition={(type) =>
                !isTrailerOrSemitrailer(type?.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
              }
            >
              <Separator spacing={1} />
            </VFKit.ShowWhen>
            <VFSubSection.DimensionsWeight />
          </VFKit.Section>
        </VStack>
      </VehicleFormDefaultLayout.Left>
      <VehicleFormDefaultLayout.Right>
        <VFKit.SubSection data-testid="additional-info">
          <AdditionalInfoWidget />
        </VFKit.SubSection>
        <Space vertical={4} />
        <VFKit.ShowWhen
          when={
            canReadBusinessCase &&
            (isCebiaAutotracerAndReportEnabled || isCebiaFeatureDecoderEnabled)
          }
        >
          <VFKit.SubSection
            data-testid="cebia-widgets"
            isExpandable={isExpandable}
            title={i18n.t('entity.vehicle.labels.integrations')}
            useCard
            cardVariant={isExpandable ? 'default' : 'inlineWhite'}
            actions={[
              {
                variant: 'link',
                type: 'button',
                leftIcon: 'action/settings',
                onClick: () => openInNewTab(settingsRoutes.vehicleServices),
                title: i18n.t('general.labels.settings'),
              },
            ]}
          >
            <VFSubSection.IntegrationWidgets vehicleId={vehicleId} />
          </VFKit.SubSection>
        </VFKit.ShowWhen>
      </VehicleFormDefaultLayout.Right>
    </VehicleFormDefaultLayout>
  );
}
